/** Clients (new with the device recorder, R1 on the canvas): the list of
 * clients this person can see, and one client's page with its Meetings tab
 * and "Record meeting". Built from data the console already loads: /api/me's
 * entities, /api/view's client rows, and /api/meetings for one client. */
import type { ClientRow, MeetingsList } from "./api.js";
import { MEETINGS_COPY } from "./meetings.js";
import { meetingTable, recordButton, retentionNote } from "./meetings-ui.js";
import { buildUrl } from "./nav.js";
import type { View } from "./nav.js";
import { el, renderSummaryStrip } from "./render.js";

function link(text: string, view: View, onNavigate: (v: View) => void, cls = "pk-meeting-link"): HTMLAnchorElement {
  const a = el("a", cls, text);
  a.href = buildUrl(view, []);
  a.addEventListener("click", (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
    ev.preventDefault();
    onNavigate(view);
  });
  return a;
}

export interface ClientsPageOptions {
  entities: ReadonlyArray<{ client_slug: string; client_name: string }>;
  rows: readonly ClientRow[]; // /api/view's, for Xero status and the next deadline
  onNavigate: (view: View) => void;
}

export function renderClientsPage(container: HTMLElement, opts: ClientsPageOptions): void {
  container.replaceChildren();
  if (!opts.entities.length) {
    container.append(el("div", "pk-empty", "No clients yet."));
    return;
  }
  const table = el("table", "pk-table pk-table-static pk-clients-table");
  const head = el("tr");
  head.append(el("th", undefined, "Client"), el("th", undefined, "Xero"), el("th", undefined, "Next deadline"));
  const thead = el("thead");
  thead.append(head);
  const tbody = el("tbody");
  for (const e of opts.entities) {
    const row = opts.rows.find((r) => r.client_slug === e.client_slug);
    const tr = el("tr");
    const name = el("td");
    name.append(link(e.client_name, { kind: "client", slug: e.client_slug, tab: "overview" }, opts.onNavigate));
    const xero = el("td", undefined, row?.xero_status_label ?? "—");
    const due = el("td");
    if (row?.next_deadline) {
      due.append(`${row.next_deadline.label} · `, el("span", "figure", formatShort(row.next_deadline.due_date)));
    } else due.append("—");
    for (const [label, cell] of [["Client", name], ["Xero", xero], ["Next deadline", due]] as const) {
      cell.dataset.label = label;
      tr.append(cell);
    }
    tbody.append(tr);
  }
  table.append(el("caption", "pk-visually-hidden", "Your clients"), thead, tbody);
  const wrap = el("div", "pk-table-wrap");
  wrap.append(table);
  container.append(wrap);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatShort(iso: string): string {
  const [, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m! - 1]}`;
}

export interface ClientPageOptions {
  slug: string;
  name: string | null; // null: not one of this person's clients
  row: ClientRow | null;
  tab: "overview" | "meetings";
  meetings: MeetingsList | null;
  meetingsError: string | null;
  timeZone: string;
  canRecord: boolean;
  onNavigate: (view: View) => void;
  onRecord: (slug: string) => void;
}

export function renderClientPage(container: HTMLElement, opts: ClientPageOptions): void {
  container.replaceChildren();
  if (!opts.name) {
    container.append(el("p", "muted", "This client isn't one you can see."));
    container.append(link("Back to Clients", { kind: "clients" }, opts.onNavigate, "pk-auth-link"));
    return;
  }
  const crumbs = el("div", "pk-crumbs");
  crumbs.append(link("Clients", { kind: "clients" }, opts.onNavigate, "pk-crumb-link"), ` / ${opts.name}`);
  const head = el("div", "pk-client-head");
  const title = el("div");
  title.append(el("h2", "pk-client-name", opts.name));
  if (opts.row) title.append(el("div", "muted pk-client-sub", `Xero: ${opts.row.xero_status_label}`));
  head.append(title);
  if (opts.canRecord) head.append(recordButton("Record meeting", () => opts.onRecord(opts.slug)));
  container.append(crumbs, head);

  const tabs = el("div", "pk-tabs");
  tabs.setAttribute("role", "tablist");
  tabs.setAttribute("aria-label", opts.name);
  for (const [tab, label] of [["overview", "Overview"], ["meetings", "Meetings"]] as const) {
    const a = link(label, { kind: "client", slug: opts.slug, tab }, opts.onNavigate, "pk-tab");
    a.setAttribute("role", "tab");
    a.setAttribute("aria-selected", String(opts.tab === tab));
    tabs.append(a);
  }
  container.append(tabs);

  const panel = el("div", "pk-tab-panel");
  panel.setAttribute("role", "tabpanel");
  if (opts.tab === "overview") {
    if (opts.row) panel.append(renderSummaryStrip(opts.row));
    else panel.append(el("div", "pk-empty", "Nothing checked with Xero for this client yet."));
  } else if (opts.meetingsError) {
    const a = el("div", "pk-alert", opts.meetingsError);
    a.setAttribute("role", "alert");
    panel.append(a);
  } else if (!opts.meetings) {
    panel.append(el("div", "pk-empty", "Loading…"));
  } else {
    const all = [...opts.meetings.to_review, ...(opts.meetings.approved ?? [])].sort((a, b) =>
      b.scheduled_start.localeCompare(a.scheduled_start)
    );
    panel.append(
      meetingTable("Meetings", all, "No meetings recorded for this client yet.", "review", { timeZone: opts.timeZone, clients: [], onNavigate: opts.onNavigate }, false)
    );
    panel.append(retentionNote(MEETINGS_COPY.clientRecordLead));
  }
  container.append(panel);
}
